import {
  Dialog,
  FlexColumn,
  DropDown,
  DropDownInputLabel,
} from '@elementary/common-ui/components';
import { FormControl } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { GPGKey } from '../../types';
import useGPGKeys from '../../hooks/useGPGKeys';
import { expiresInDaysOptions, keyLengthOptions } from '../../constants';

interface GenerateGPGKeyForm {
  keyLength: number;
  expiresInDays: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (key: GPGKey) => void;
}

const GenerateStageDialog = ({ isOpen, onClose, onSuccess }: Props) => {
  const { generateKey, isGenerating } = useGPGKeys();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<GenerateGPGKeyForm>({
    defaultValues: {
      keyLength: 3072,
      expiresInDays: -1,
    },
  });

  const onSubmit = (data: GenerateGPGKeyForm) => {
    generateKey(
      {
        keyLength: data.keyLength,
        expiresInDays: data.expiresInDays,
      },
      {
        onSuccess: (generatedKey) => {
          onSuccess(generatedKey);
        },
      },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Generate GPG Key"
      confirmText="Generate Key"
      isLoading={isGenerating}
      onConfirmButtonClick={handleSubmit(onSubmit)}
      customWidth={600}
      message={
        <form>
          <FlexColumn sx={{ gap: 3 }}>
            <Controller
              name="keyLength"
              control={control}
              rules={{ required: 'Key length is required' }}
              render={({ field }) => (
                <FormControl fullWidth>
                  <DropDownInputLabel>Key Length</DropDownInputLabel>
                  <DropDown
                    {...field}
                    size="large"
                    value={field.value.toString()}
                    onChange={(value) => field.onChange(Number(value))}
                    error={!!errors.keyLength}
                    options={keyLengthOptions.map((option) => ({
                      value: option.value.toString(),
                      label: option.label,
                    }))}
                  />
                </FormControl>
              )}
            />

            <Controller
              name="expiresInDays"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <DropDownInputLabel>Expiration</DropDownInputLabel>
                  <DropDown
                    {...field}
                    size="large"
                    value={field.value?.toString() || '-1'}
                    onChange={(value) => field.onChange(Number(value))}
                    options={expiresInDaysOptions.map((option) => ({
                      value: option.value.toString(),
                      label: option.label,
                    }))}
                    sortOptions={false}
                    placeholder="Select expiry option"
                    error={!!errors.expiresInDays}
                  />
                </FormControl>
              )}
            />
          </FlexColumn>
        </form>
      }
    />
  );
};

export default GenerateStageDialog;
